/* eslint-disable */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { isArray, get } from 'lodash';
import { ExpandCollapse, Typography } from '@telus-uds/components-web';
import FormSummaryRow from '../formSummaryRow';
import { GroupWrapper, GroupTitle } from '../css';
import {
  additionalEquipment,
  easyAdditionalEquipment,
  oneTimeAdditionalEquipment,
  fieldWinbackFibre,
  valueNewTelusBusinessOrder,
  fieldWinback,
  statementOfWork,
  appendices
} from '../../utils/constant';
import { getLocalizedStrings } from '../../misc';
import request from '../../utils/request';
import PricingAndEstimates from '../fmSOWComponents/pricingAndEstimates/pricingAndEstimates';

const userDetailsKey = 'userDetails';
const formSectionKey = 'formSection';
const repeatedFormKey = 'repeatedForm';

const removePrice = label => {
  if (!label || typeof label !== 'string') return label;
  return label.replace(/\([\D\s]*\$[\d.\s\D]*\)/g, '');
};

const priceClean = label => {
  if (Array.isArray(label)) {
    return label.map(filteredLabel => removePrice(filteredLabel));
  }
  return removePrice(label);
};

export const getFieldLabel = (options, fieldValue) => {
  if (!options || !options.length) {
    if (Array.isArray(fieldValue)) {
      return (
        fieldValue &&
        fieldValue.length > 0 &&
        fieldValue.map(field => priceClean(field))
      );
    }
    return priceClean(fieldValue);
  }
  for (let i = 0; i < options.length; i += 1) {
    const option = options[i];
    const fields = option.fields || {};
    const { label, value } = fields || {};
    if (value === fieldValue) {
      return priceClean(label);
    }
  }

  if (Array.isArray(fieldValue) && fieldValue.length === 0) {
    return null;
  }
  return priceClean(fieldValue);
};

const profile = request.getProfile();

const FMSowOrderSummary = ({ stepData, orderData }) => {
  const {
    contentfulValues: { yes, no }
  } = getLocalizedStrings();

  const summaryStepsData = useMemo(() => {
    const stepsData = (stepData && [...stepData]) || [];
    stepsData.splice(stepsData.length - 1, 1);
    // Statement of Work / Appendices are rendered as their own top-level panels below —
    // exclude them here so they don't also show up as duplicate nested panels under Customer Information.
    return stepsData.filter(
      step => ![statementOfWork, appendices].includes(get(step, 'fields.backendKey'))
    );
  }, [stepData]);

  const groupSummary = (
    fieldNamesArray,
    formStepFieldsArray,
    groupOrderData
  ) => {
    return formStepFieldsArray.map((formStepFields, index) => {
      if (!formStepFields) return null;
      const nonEmptyFields =
        formStepFields &&
        formStepFields.filter(currentField => {
          const {
            fields: { backendKey }
          } = currentField || {};
          const isObject = typeof groupOrderData[backendKey] === 'object';
          const isArray = Array.isArray(groupOrderData[backendKey]);
          if (isObject && !isArray) {
            return !!groupOrderData[backendKey].value;
          }
          if (isArray && groupOrderData[backendKey].length) {
            return true;
          }
          return !!groupOrderData[backendKey];
        });

      if (!nonEmptyFields || !nonEmptyFields.length) return null;
      const setTitle = label => {
        if (label === additionalEquipment) {
          return `${label} (${oneTimeAdditionalEquipment})`;
        }
        if (label === easyAdditionalEquipment) {
          return `${additionalEquipment} (${label})`;
        }
        return label;
      };
      return (
        <GroupWrapper style={{ marginBottom: '1em', paddingLeft: 0, paddingRight: 0 }}>
          <GroupTitle style={{ marginBottom: '1em' }}>
            {setTitle(fieldNamesArray[index])}
          </GroupTitle>
          {nonEmptyFields &&
            nonEmptyFields.map(({ fields: { backendKey, label, options } }) => {
              let fieldValue = '';
              const isArray = Array.isArray(groupOrderData[backendKey]);
              const isObject = typeof groupOrderData[backendKey] === 'object';
              if (isObject && !isArray) {
                fieldValue = priceClean(groupOrderData[backendKey].value);
              } else if (isArray) {
                fieldValue = groupOrderData[backendKey]
                  .join(', ')
                  .split(',')
                  .map(item => priceClean(item))
                  .join(',');
              } else {
                fieldValue = priceClean(groupOrderData[backendKey]);
              }
              const labelLogic =
                fieldNamesArray[index] &&
                fieldNamesArray[index] === additionalEquipment
                  ? priceClean(label)
                  : priceClean(label);
              return (
                <FormSummaryRow
                  label={labelLogic}
                  value={getFieldLabel(options, fieldValue)}
                  isDollarRemove
                />
              );
            })}
        </GroupWrapper>
      );
    });
  };

  const handleFormSummaryRow = (
    {
      fieldLabel,
      fieldBackendKey,
      fieldName,
      formStepFields,
      contentfulId,
      formTitle,
      options: mainOptions
    },
    customOrderData
  ) => {
    const dataOrder = customOrderData || orderData;

    if (
      (fieldName === fieldWinbackFibre || fieldName === fieldWinback) &&
      dataOrder.orderType !== valueNewTelusBusinessOrder
    )
      return null;
    if (contentfulId === userDetailsKey) {
      return groupSummary([fieldName], [formStepFields], dataOrder);
    }
    if (contentfulId === formSectionKey) {
      return (
        formStepFields &&
        formStepFields.length > 0 &&
        formStepFields.map(item => {
          const {
            fields: {
              label,
              backendKey,
              name: sectionFieldName,
              fields: groupFields,
              formFields: sectionFormFields,
              title,
              formTitle: sectionFormTitle
            },
            sys: {
              contentType: {
                sys: { id: fieldContentfulId }
              }
            }
          } = item || {};
          const rowObj = {
            fieldLabel: label,
            fieldBackendKey: backendKey,
            fieldName: sectionFieldName || title,
            formStepFields: groupFields || sectionFormFields,
            contentfulId: fieldContentfulId,
            formTitle: title || sectionFormTitle
          };
          return handleFormSummaryRow(rowObj, customOrderData);
        })
      );
    }
    if (contentfulId === repeatedFormKey) {
      return (
        dataOrder[fieldName] &&
        dataOrder[fieldName].map((row, idx) =>
          groupSummary([`${formTitle} ${idx + 1}`], [formStepFields], row)
        )
      );
    }
    const fieldKey =
      (dataOrder[fieldBackendKey] && fieldBackendKey) || fieldName;
    let fieldValue = getFieldLabel(mainOptions, dataOrder[fieldKey]);

    // Check if the type can be converted to float
    let typeCheckedFieldValue = parseFloat(fieldValue);

    // Check if the value really is in decimals and needs rounding off
    if (typeCheckedFieldValue && typeCheckedFieldValue % 1 !== 0) {
      typeCheckedFieldValue = typeCheckedFieldValue.toFixed(2);
    } else if (typeof fieldValue === 'boolean') {
      typeCheckedFieldValue = fieldValue ? yes : no;
    } else {
      typeCheckedFieldValue = fieldValue;
    }

    return (
      <FormSummaryRow
        label={fieldLabel}
        value={typeCheckedFieldValue}
        isDollarRemove
      />
    );
  };

  const rowCb = (formStepFields, customOrderData) => {
    // function to replace key names in data
    const replaceKeyInArray = (array, targetKey, newKey) => {
      return array.map(obj => {
        const { fields, ...rest } = obj;
        let updatedNestedObject = fields;

        if (fields[targetKey] && obj.fields[formStepFields]) {
          updatedNestedObject = {
            ...fields,
            [newKey]: fields[targetKey]
          };
        }

        return { ...rest, fields: updatedNestedObject };
      });
    };

    const updatedArray = formStepFields
      .map(obj => obj.fields.formFields)
      .flat();

    const updatedObject = replaceKeyInArray(
      updatedArray,
      'formFields',
      'fields'
    );

    return updatedArray.map(
      ({
        fields: {
          label: fieldLabel,
          outputLabel,
          backendKey: fieldBackendKey,
          formStepFields: subStepFields,
          formFields: repeatedGroupFields,
          name: fieldName,
          title: groupTitle,
          formTitle,
          options
        },
        sys: {
          contentType: {
            sys: { id: contentfulId }
          }
        }
      }) => (
        <>
          {handleFormSummaryRow(
            {
              fieldLabel: outputLabel || fieldLabel,
              fieldBackendKey,
              formStepFields:
                subStepFields || repeatedGroupFields || formStepFields,
              fieldName: fieldName || groupTitle,
              contentfulId,
              formTitle: formTitle || groupTitle,
              options
            },
            customOrderData,
            formStepFields
          )}
        </>
      )
    );
  };

  const alphabets = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  return (
    <ExpandCollapse>
      {(expandProps) => (
        <>
          <ExpandCollapse.Panel
            {...expandProps}
            panelId={'customerInformation'}
            control={<Typography variant={{ size: 'h2' }}>Customer Information</Typography>}
          >
            {summaryStepsData &&
              summaryStepsData.map(
                ({ fields: { backendKey: stepBackendKey, formStepFields } }) => (
                  <React.Fragment key={stepBackendKey}>
                    {formStepFields && rowCb(formStepFields)}
                  </React.Fragment>
                )
              )}
          </ExpandCollapse.Panel>

          <ExpandCollapse.Panel
            {...expandProps}
            panelId={'statementOfWork'}
            control={<Typography variant={{ size: 'h2' }}>Statement of Work</Typography>}
            key={'statementOfWork'}
          >
            {orderData && (
              <>
                <PricingAndEstimates
                  defaultValues={get(orderData, 'pricingAndEstimates', {})}
                  isEditable={false}
                  orderData={orderData}
                  onChange={e => {}}
                />
                {orderData.paymentPlanTerm && (
                  <FormSummaryRow
                    label={'Project Payment Plan Term'}
                    value={orderData.paymentPlanTerm}
                  />
                )}
                {orderData.appendicesIncluded && (
                  <FormSummaryRow
                    label={'Are there any appendices that need to be included?'}
                    value={orderData.appendicesIncluded}
                  />
                )}
              </>
            )}
          </ExpandCollapse.Panel>

          {orderData.appendices && (
            <ExpandCollapse.Panel
              {...expandProps}
              panelId={'appendices'}
              control={<Typography variant={{ size: 'h2' }}>Appendices</Typography>}
              key={'appendices'}
            >
              {orderData &&
                orderData.appendices.length &&
                orderData.appendices.map((appendice, index) => {
                  return (
                    <FormSummaryRow
                      key={index}
                      label={'Appendix ' + alphabets[index]}
                      value={appendice.appendixTitle}
                    />
                  );
                })}
            </ExpandCollapse.Panel>
          )}
        </>
      )}
    </ExpandCollapse>
  );
};

FMSowOrderSummary.propTypes = {
  stepData: PropTypes.objectOf(PropTypes.any).isRequired,
  orderData: PropTypes.objectOf(PropTypes.any).isRequired
};

export default FMSowOrderSummary;
